import { ProductCategory } from "../__generated__/graphql";

/**
 * Per-form configuration for shell forms.
 *
 * A "shell form" captures exactly: diagnoses, a length of need, free-text
 * products (name / size / quantity / remarks), a medical-necessity attestation
 * and an optional preferred DME company. Several categories capture precisely
 * that and differ only in name and clinical copy, so they share one component
 * and one set of tables, discriminated by `productCategory`.
 *
 * Adding a shell form is an entry here plus the matching backend
 * `SHELL_FORM_CONFIGS` entry, a `categoryToTabIndex` position and an icon. A form
 * that needs ANY field beyond the list above is not a shell form — give it its
 * own tables and component via the `create-new-form` skill, as Nebulizer has.
 *
 * Lives in shared-components because the provider form, the SWO PDF and the
 * Progress Note summary all need the display name.
 *
 * Keep in step with `services/orders/shell_form_config.py`. The DOM ids below are
 * derived by the same two rules on both sides, so neither can be edited alone.
 */
export interface ShellFormConfig {
  category: ProductCategory;
  /** Shown as the tab label and section headings. */
  displayName: string;
  /**
   * What the provider attests to in Medical Necessity, rendered on the form and
   * printed on the progress note. A legal statement: copy it verbatim from
   * clinical, never paraphrase.
   */
  medicalNecessity: ShellFormMedicalNecessity;
}

/** One criteria bullet, with optional sub-bullets (two levels at most). */
export interface MedicalNecessityCriterion {
  text: string;
  children?: string[];
}

export interface ShellFormMedicalNecessity {
  criteria: MedicalNecessityCriterion[];
  /** The coverage policy the criteria come from, e.g. "... LCD guidelines". */
  source?: string;
}

export const SHELL_FORM_CONFIG: Partial<
  Record<ProductCategory, ShellFormConfig>
> = {
  [ProductCategory.PneumaticCompression]: {
    category: ProductCategory.PneumaticCompression,
    displayName: "Pneumatic Compression",
    medicalNecessity: {
      criteria: [
        {
          text: "The patient has at least one of two diagnoses:",
          children: [
            "Lymphedema, or",
            "Chronic venous insufficiency (CVI) of the lower extremities with one or more venous stasis ulcer(s)",
          ],
        },
        {
          text: "The patient has undergone a conservative therapy trial (4 weeks for lymphedema, 6 months for CVI) that has been documented as failed or insufficient",
        },
      ],
      source: "Pneumatic Compression Devices NCD guidelines",
    },
  },
  [ProductCategory.CoughStimulating]: {
    category: ProductCategory.CoughStimulating,
    displayName: "Cough Stimulating",
    medicalNecessity: {
      criteria: [
        {
          text: "The patient has difficulty raising and clearing secretions as a result of one of the following:",
          children: [
            "Cancer or surgery of the throat or mouth",
            "Dysfunction of the swallowing muscles",
            "Unconsciousness or obtunded state",
            "Tracheostomy",
            "Use of a respiratory suction pump for other conditions will be denied as not reasonable and necessary.",
          ],
        },
      ],
    },
  },
  [ProductCategory.Tracheostomy]: {
    category: ProductCategory.Tracheostomy,
    displayName: "Tracheostomy",
    medicalNecessity: {
      criteria: [
        {
          text: "The patient has had an open surgical tracheostomy that has been open or is expected to remain open for at least three months.",
        },
        {
          text: "Quantity ordered is within the usual maximum per month for each supply",
          children: [
            "OR I attest that the medical record documents why a quantity above the usual maximum is needed",
          ],
        },
      ],
      source: "Tracheostomy Care Supplies LCD guidelines",
    },
  },
  [ProductCategory.ElectricalStimulators]: {
    category: ProductCategory.ElectricalStimulators,
    displayName: "Electrical Stimulators",
    medicalNecessity: {
      criteria: [
        {
          text: "You are the treating practitioner for the condition justifying the device",
        },
        {
          text: "The patient has one of two indications:",
          children: [
            "Acute post-operative pain, ordered within 30 days of surgery, rental only,",
            "OR Chronic intractable pain other than low back pain, present for at least three months, with other treatment modalities tried and failed",
          ],
        },
        {
          text: "For chronic pain, the patient has completed a monitored trial rental of one to two months and is likely to benefit from long-term use",
        },
        {
          text: "A standard 2-lead setup is sufficient",
          children: [
            "OR I attest that I have explicit clinical proof showing why a standard 2-lead setup is insufficient",
          ],
        },
        {
          text: "If ordering E0731, the patient cannot use conventional electrodes because of the size or number of sites, site inaccessibility, a documented skin condition, or a cast",
        },
      ],
      source: "Transcutaneous Electrical Nerve Stimulators LCD guidelines",
    },
  },
};

/** Every registered shell-form category, in a stable order. */
export const SHELL_FORM_CATEGORIES = Object.keys(
  SHELL_FORM_CONFIG,
).sort() as ProductCategory[];

export const isShellFormCategory = (category: ProductCategory): boolean =>
  category in SHELL_FORM_CONFIG;

/**
 * `PNEUMATIC_COMPRESSION` -> `PneumaticCompression`. Mirrors `_pascal_case` in
 * `shell_form_config.py`.
 */
const pascalCase = (category: ProductCategory): string =>
  category
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join("");

/**
 * DOM id of the tab button. The backend puts this in each validation error's
 * `switchToTab` so the error link can open the right tab, so the two derivations
 * must agree exactly.
 */
export const shellFormTabId = (category: ProductCategory): string =>
  `${pascalCase(category)}-Tab`;

/**
 * Prefix for the form's scroll-anchor ids. The backend derives the same prefix
 * for each error's `scrollTo`, so these ids must exist in the rendered form.
 */
export const shellFormAnchorPrefix = (category: ProductCategory): string =>
  category.toLowerCase();

/**
 * Path to the tab icon, served from `services/versemedical/public/icons`. The
 * file has to exist under exactly this name or the tab renders a broken image,
 * so registering a form means adding the SVG too.
 */
export const shellFormIconPath = (category: ProductCategory): string =>
  `/icons/${pascalCase(category)}.svg`;

/**
 * Prefix for `data-testid` / `data-cy` values, kebab-cased:
 * `PNEUMATIC_COMPRESSION` -> `pneumatic-compression`.
 *
 * Derived rather than hardcoded so two shell forms rendered on the same page
 * cannot collide on a shared id. Chosen to reproduce the ids the Pneumatic
 * Compression form already used, so no existing selector changes.
 */
export const shellFormTestIdPrefix = (category: ProductCategory): string =>
  category.toLowerCase().replace(/_/g, "-");
